import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface CloverOptions {
  merchantId?: string;
  locale?: 'en-US' | 'en-CA' | 'fr-CA';
}

export interface CloverElementStyles {
  [key: string]: Record<string, string>;
}

export type CloverElementType =
  | 'CARD_NUMBER'
  | 'CARD_DATE'
  | 'CARD_CVV'
  | 'CARD_POSTAL_CODE'
  | 'CARD_NAME'
  | 'CARD_STREET_ADDRESS'
  | 'PAYMENT_REQUEST_BUTTON';

export interface CloverFieldEvent {
  CARD_NUMBER?: CloverFieldState;
  CARD_DATE?: CloverFieldState;
  CARD_CVV?: CloverFieldState;
  CARD_POSTAL_CODE?: CloverFieldState;
  CARD_NAME?: CloverFieldState;
  CARD_STREET_ADDRESS?: CloverFieldState;
}

export interface CloverFieldState {
  error?: string;
  touched?: boolean;
}

export interface CloverElements {
  create(type: CloverElementType, styles?: CloverElementStyles): CloverElement;
}

export interface CloverElement {
  mount(selector: string): void;
  destroy(): void;
  addEventListener(
    event: 'change' | 'blur',
    callback: (event: CloverFieldEvent) => void
  ): void;
}

export interface CloverInstance {
  elements(): CloverElements;
  createToken(): Promise<CloverTokenResult>;
}

export interface CloverTokenResult {
  token?: string;
  id?: string;
  errors?: Record<string, string>;
}

export interface CloverChargeRequest {
  order_id: number;
  token: string;
}

export interface CloverChargeResponse {
  success: boolean;
  [key: string]: any;
}

export class CloverChargeError extends Error {
  constructor(
    public readonly orderId: number,
    public readonly originalError?: unknown
  ) {
    super(`Clover charge failed for order #${orderId}`);
    this.name = 'CloverChargeError';
  }
}

declare var Clover: new (
  apiAccessKey: string,
  options?: CloverOptions
) => CloverInstance;

const CLOVER_SDK_URLS = {
  production: 'https://checkout.clover.com/sdk.js',
  sandbox: 'https://checkout.sandbox.dev.clover.com/sdk.js'
} as const;

@Injectable({
  providedIn: 'root'
})
export class CloverPaymentService {
  private cloverInstance: CloverInstance | null = null;
  private sdkPromise: Promise<void> | null = null;
  private readonly chargeUrl = `${environment.happyApi?.baseUrl}/payments/clover/charge`

  private readonly messageGuard = (e: MessageEvent): void => {
    if (typeof e.data !== 'string') {
      e.stopImmediatePropagation();
    }
  };
  private messageGuardActive = false;

  constructor(private httpClient: HttpClient) {}

  public get isConfigured(): boolean {
    return !!(environment as any)?.clover?.publicKey;
  }

  public async init(): Promise<CloverInstance> {
    if (this.cloverInstance) {
      return this.cloverInstance;
    }

    this.addMessageGuard();
    await this.ensureSdkLoaded();

    const cloverConfig = (environment as any)?.clover;
    if (!cloverConfig?.publicKey) {
      throw new Error('Clover public key is not configured in environment.');
    }

    const options: CloverOptions = {};
    if (cloverConfig.merchantId) {
      options.merchantId = cloverConfig.merchantId;
    }

    this.cloverInstance = new Clover(cloverConfig.publicKey, options);
    return this.cloverInstance;
  }

  public charge(
    payload: CloverChargeRequest
  ): Observable<CloverChargeResponse> {
    return this.httpClient.post<CloverChargeResponse>(this.chargeUrl, payload);
  }

  public chargeOrder(
    orderId: number,
    token: string
  ): Observable<CloverChargeResponse> {
    return this.charge({
      order_id: orderId,
      token
    }).pipe(
      catchError((err) => {
        console.error('Clover charge failed:', err);
        return throwError(() => new CloverChargeError(orderId, err));
      })
    );
  }

  public destroy(): void {
    this.cloverInstance = null;
    this.sdkPromise = null;
    this.removeCloverIframes();
    this.removeScript();
    this.deleteGlobalClover();
    this.removeMessageGuard();
  }

  private addMessageGuard(): void {
    if (!this.messageGuardActive) {
      window.addEventListener('message', this.messageGuard, { capture: true });
      this.messageGuardActive = true;
    }
  }

  private removeMessageGuard(): void {
    if (this.messageGuardActive) {
      window.removeEventListener('message', this.messageGuard, { capture: true });
      this.messageGuardActive = false;
    }
  }

  private removeCloverIframes(): void {
    document
      .querySelectorAll<HTMLIFrameElement>('iframe[src*="clover"]')
      .forEach((iframe) => iframe.remove());
  }

  private deleteGlobalClover(): void {
    try {
      delete (window as any).Clover;
    } catch { /* non-configurable in some envs */ }
  }

  private removeScript(): void {
    const urls = Object.values(CLOVER_SDK_URLS) as string[];
    document.querySelectorAll<HTMLScriptElement>('script').forEach((script) => {
      if (urls.some((url) => script.src === url)) {
        script.remove();
      }
    });
  }

  private ensureSdkLoaded(): Promise<void> {
    if (typeof Clover !== 'undefined') {
      return Promise.resolve();
    }

    if (this.sdkPromise) {
      return this.sdkPromise;
    }

    this.sdkPromise = new Promise<void>((resolve, reject) => {
      const isSandbox = (environment as any)?.clover?.isSandbox ?? false;
      const sdkUrl = isSandbox
        ? CLOVER_SDK_URLS.sandbox
        : CLOVER_SDK_URLS.production;

      const script = document.createElement('script');
      script.src = sdkUrl;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Clover SDK.'));
      document.head.appendChild(script);
    });

    return this.sdkPromise;
  }
}
